/**
 * What to tell the recruiter after clearing a request, given what happened to their mailbox.
 *
 * Pure, and split out of RequestsTab both to keep that file under its line ceiling and because the
 * honesty rule here is worth testing directly: **the request is handled in every branch.** Only the
 * Gmail tidy-up varies. So none of these is an error toast — reporting a failure for work that
 * succeeded would send the recruiter looking for a request that is correctly gone.
 *
 * The flip side of that rule: only the `updated` branch may say anything about the MAILBOX. For
 * `needs_reconnect`, `failed` and `skipped` the thread can still be sitting there unread and in the
 * inbox, so those branches claim our own record and nothing else — "marked as read" there was a
 * statement about Gmail that Gmail had not agreed to.
 *
 * `reconnect` is the one outcome with an action attached: the recruiter's token predates the
 * `gmail.modify` scope, and the connect banner is how they fix it.
 *
 * `archive` distinguishes the two buttons, and only in the wording: "archived in Gmail" and "left in
 * your inbox" are different promises about where the mail now is, and saying the wrong one sends a
 * recruiter hunting through the wrong folder.
 */
export interface DismissOutcome {
  status: 'success' | 'info';
  description: string;
  /** True when the connect banner should be raised (re-consent needed for the modify scope). */
  reconnect: boolean;
}

export function dismissOutcome(
  candidateName: string,
  gmail: 'updated' | 'failed' | 'skipped' | 'needs_reconnect' | undefined,
  archive = true
): DismissOutcome {
  if (gmail === 'needs_reconnect') {
    return {
      status: 'info',
      description: archive
        ? `${candidateName} removed from the overview — reconnect Gmail to also tidy your inbox.`
        : `${candidateName} removed from the overview — reconnect Gmail to also mark the mail read.`,
      reconnect: true,
    };
  }
  if (gmail === 'failed') {
    return {
      status: 'info',
      description: `${candidateName} removed from the overview — Gmail could not be updated.`,
      reconnect: false,
    };
  }
  if (gmail === 'updated') {
    return {
      status: 'success',
      description: archive
        ? `${candidateName} marked as read and archived in Gmail.`
        : `${candidateName} marked as read and done — the mail stays in your inbox.`,
      reconnect: false,
    };
  }
  // 'skipped' or an older proxy that reports nothing: claim only what we know happened, which is
  // our own record — never that the mailbox changed.
  return {
    status: 'success',
    description: `${candidateName} removed from the overview.`,
    reconnect: false,
  };
}
